<script setup lang="ts">
import type {
  FormField,
  FormFieldChoice,
  GraphData,
  GraphDataSet,
  GraphDatasetData
} from '@/models';
import Dialog from 'primevue/dialog';
import { computed, onMounted, ref, watch, type PropType } from 'vue';
import Chart from 'primevue/chart';
import * as ChartJS from 'chart.js/auto';
import Checkbox from 'primevue/checkbox';
import { VerticalLinePlugin } from '@/utils/chartJsHelper';

const props = defineProps({
  dialogLabel: {
    type: String,
    required: true
  },
  visible: {
    type: Boolean,
    required: true
  },
  field: {
    type: Object as PropType<FormField>,
    required: true
  },
  timeSeriesTimepoints: {
    type: Object as PropType<FormFieldChoice[]>,
    required: true
  }
});

const chartType = computed(() => {
  return props.field.plotType === 'Barplot' ? 'bar' : 'line';
});

const graphOptions = computed(() => {
  if (chartType.value === 'bar') {
    return {
      scales: {
        x: {
          type: 'category',
          stacked: true,
          title: {
            display: true,
            text: 'Perfusion Timepoint',
            font: {
              size: 14,
              weight: 'bold'
            },
            color: '#333'
          }
        },
        y: {
          beginAtZero: true,
          stacked: false,
          title: {
            display: true,
            text: props.field.label,
            font: {
              size: 14,
              weight: 'bold'
            },
            color: '#333'
          }
        }
      },
      responsive: true,
      maintainAspectRatio: true,
      plugins: {
        legend: {
          display: true,
          labels: {
            filter: (legendItem: unknown, data: unknown) => {
              // Only show legend for datasets with a non-empty label
              //@ts-expect-error just the unknown datatypes
              return (data.datasets[legendItem.datasetIndex].label !== '');
            }
          },
          generateLabels: (chart: ChartJS.Chart) => {
            return chart.data.datasets.map((dataset: ChartJS.ChartDataset, index) => ({
              text: dataset.label,
              fillStyle: dataset.backgroundColor,
              strokeStyle: dataset.borderColor,
              hidden: !chart.isDatasetVisible(index),
              pointStyle: dataset.label == 'Active liver' || dataset.label == 'Historical median' ? 'circle' : 'rect',
              pointStyleWidth: dataset.label === 'Active liver' || dataset.label == 'Historical median' ? undefined : 40,
              datasetIndex: index
            }));
          },
          usePointStyle: true
        }
      }
    };
  }
  
  return {
    scales: {
      x: {
        type: 'linear',
        position: 'bottom',
        ticks: {
          stepSize: 1
        },
        title: {
          display: true,
          text: 'Perfusion time (hours)',
          font: {
            size: 14,
            weight: 'bold'
          },
          color: '#333'
        }
      }
    },
    layout: {
      padding: 0
    },
    events: ['mousemove', 'mouseout', 'click', 'touchstart', 'touchmove'],
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    },
    plugins: {
      verticalLines: [
        {
          x: 0,
          color: 'black',
          lineWidth: 1,
          dash: [6, 6]
        }
      ],
      tooltip: {
        events: ['click', 'mousemove']
      },
      legend: {
        labels: {
          filter: (legendItem: unknown, data: unknown) => {
            // Only show legend for datasets with a non-empty label
            //@ts-expect-error just the unknown datatypes
            return (data.datasets[legendItem.datasetIndex].label !== '' &&
              //@ts-expect-error just the unknown datatypes
              data.datasets[legendItem.datasetIndex].label !== 'CIlower' && data.datasets[legendItem.datasetIndex].label !== 'CIupper') &&
              //@ts-expect-error just the unknown datatypes
              data.datasets[legendItem.datasetIndex].label !== 'CIlower2' && data.datasets[legendItem.datasetIndex].label !== 'CIupper2';
          }
        }
      }
    },
    animation: false,
    spanGaps: true,
    responsive: true,
    maintainAspectRatio: true,
    datasets: {
      line: {
        pointRadius: 4,
        pointHoverRadius: 4,
        pointHoverBorderWidth: 4,
        pointBorderColor: '#0265de',
        pointHoverBorderColor: '#0265de'
      }
    },
    elements: {
      point: {
        radius: 0
      },
      line: {
        tension: 0.4,
        borderWidth: 3,
        hoverBorderWidth: 5,
        backgroundColor: '#0265de',
        borderColor: '#0265de',
        borderCapStyle: 'round',
        borderJoinStyle: 'round'
      }
    }
  };
});

onMounted(() => {
  ChartJS.Chart.register(VerticalLinePlugin);
  prepareGraphData();
});

const emits = defineEmits(['onClose']);

const closeDialog = async () => {
  emits('onClose');
};

const graphData = ref<GraphData>();
const showBaseLineData = ref<boolean>(false);

watch(() => showBaseLineData.value, () => {
  prepareGraphData();
});

const prepareGraphData = () => {
  if (chartType.value === 'bar') {
    const labels: string[] = [];
    const rangeData: ([number, number] | null)[] = [];
    const range2Data: ([number, number] | null)[] = [];
    const historicalValueData: ([number, number] | null)[] = [];
    const valueData: ([number, number] | null)[] = [];

    props.field.timeSeriesData?.forEach(data => {
      if (data.value) {
        valueData.push([Number(data.value), data.value]);
      }
    });

    const allTimepoints = props.field.historicalData?.map(
      item => item.perfusionActualTimepoint || `${item.perfusionSelectedTimepoint}`
    ) || [];

    allTimepoints.forEach(timepoint => {
      labels.push(`${timepoint}`);
      const item = props.field.historicalData?.find(
        d => (d.perfusionActualTimepoint || `${d.perfusionSelectedTimepoint}`) === timepoint
      );

      rangeData.push(item && item.ymin != null && item.ymax != null ? [Number(item.ymin), Number(item.ymax)] : null);
      range2Data.push(item && item.ymin2 != null && item.ymax2 != null ? [Number(item.ymin2), Number(item.ymax2)] : null);
      
      if (item) {
        historicalValueData.push([Number(item.value), Number(item.value)]);
      } else {
        historicalValueData.push(null);
      }
    });

    const datasets: GraphDataSet[] = [];

    datasets.push({
      label: 'Active liver',
      type: 'scatter',
      data: valueData,
      backgroundColor: ['#006ef5'],
      pointRadius: 8, // Size of the points
      pointHoverRadius: 10
    });

    // Dataset for the value "point", drawn as a thin bar/line
    datasets.push({
      label: 'Historical median',
      type: 'scatter',
      data: historicalValueData,
      backgroundColor: ['#ff0000'],
      pointRadius: 8, // Size of the points
      pointHoverRadius: 10
    });

    // Dataset for the faint, outer range
    datasets.push({
      label: '95% tolerance interval',
      data: range2Data,
      backgroundColor: ['rgba(2, 101, 222, 0.25)'],
      barPercentage: 0.2
    });

    // Dataset for the dark, inner range
    datasets.push({
      label: '95% CI of median',
      data: rangeData,
      backgroundColor: ['rgba(2, 101, 222, 0.6)'],
      barPercentage: 0.2
    });

    graphData.value = {
      labels: labels,
      datasets: datasets
    };
    return;
  }

  const values: (GraphDatasetData|null)[] = [];
  if (props.field.cumulativeTimeSeriesData) {
    props.field.cumulativeTimeSeriesData.forEach(data => {
      values.push({
        x: data.perfusionActualTimepoint!,
        y: data.value
      });
    });
  } else {
    props.field.timeSeriesData?.forEach(data => {
      if (data.value) {
        values.push({
          x: data.perfusionActualTimepoint!,
          y: data.value
        });
      }
    });
  }

  const dataset: GraphDataSet = {
    label: 'Active liver',
    data: values
  };

  const pumpValues: GraphDatasetData[] = [];
  const pumpMinValues: GraphDatasetData[] = [];
  const pumpMaxValues: GraphDatasetData[] = [];
  const pumpMinValues2: GraphDatasetData[] = [];
  const pumpMaxValues2: GraphDatasetData[] = [];

  for (const data of props.field.historicalData || []) {
    if (!showBaseLineData.value && data.perfusionActualTimepoint < 0) {
      continue;
    }

    pumpValues.push({
      x: data.perfusionActualTimepoint!,
      y: data.value
    });
    pumpMinValues.push({
      x: data.perfusionActualTimepoint,
      y: data.ymin
    });
    pumpMaxValues.push({
      x: data.perfusionActualTimepoint,
      y: data.ymax
    });
    // if (data.ymin2 !== null && data.ymin2 !== undefined && data.ymax2 !== null && data.ymax2 !== undefined) {
    //   pumpMinValues2.push({
    //     x: data.perfusionActualTimepoint,
    //     y: data.ymin2
    //   });
    //   pumpMaxValues2.push({
    //     x: data.perfusionActualTimepoint,
    //     y: data.ymax2
    //   });
    // }
  }

  const pumpLiverDataset: GraphDataSet = {
    label: 'PUMP livers',
    data: pumpValues,
    backgroundColor: ['#bf02ef'],
    borderColor: ['#bf02ef'],
    pointRadius: 0,
    pointHoverRadius: 4,
    pointBorderColor: '#bf02ef',
    pointHoverBorderColor: '#bf02ef'
  };

  const minDataset: GraphDataSet = {
    label: 'CIlower',
    pointRadius: 0,
    pointHoverRadius: 0,
    data: pumpMinValues,
    borderColor: ['rgba(0,0,0,0)'], 
    backgroundColor: ['rgba(0, 200, 0, 0.5)'],
    fill: '+1'
  };

  const maxDataset: GraphDataSet = {
    label: 'CIupper',
    data: pumpMaxValues,
    pointRadius: 0,
    pointHoverRadius: 0,
    borderColor: ['rgba(0,0,0,0)'], 
    backgroundColor: ['rgba(0, 200, 0, 0.5)'],
    fill: false
  };

  const minDataset2: GraphDataSet = {
    label: 'CIlower2',
    pointRadius: 0,
    pointHoverRadius: 0,
    data: pumpMinValues2,
    borderColor: ['rgba(0,0,0,0)'], 
    backgroundColor: ['rgba(0, 200, 0, 0.25)'],
    fill: '+1'
  };

  const maxDataset2: GraphDataSet = {
    label: 'CIupper2',
    data: pumpMaxValues2,
    pointRadius: 0,
    pointHoverRadius: 0,
    borderColor: ['rgba(0,0,0,0)'], 
    backgroundColor: ['rgba(0, 200, 0, 0.25)'],
    fill: false
  };

  const markerPoints: GraphDatasetData[] = [];

  props.field.bicarbonateData?.forEach(data => {
    markerPoints.push({
      x: data.perfusionActualTimepoint!,
      y: data.value,
      //@ts-expect-error: we are using a custom property
      volume: data.volume
    });
  });

  const markerDataset: GraphDataSet = {
    label: 'Sodium Bicarbonate (8.4%)',
    type: 'scatter',
    data: markerPoints,
    backgroundColor: ['#ff0000'],
    borderColor: ['#ff0000'],
    pointRadius: 8
  };

  const datasets = [dataset, pumpLiverDataset, minDataset, maxDataset];

  if (pumpMinValues2.length > 0 && pumpMaxValues2.length > 0) {
    datasets.push(minDataset2);
    datasets.push(maxDataset2);
  }

  if (props.field.bicarbonateData && props.field.bicarbonateData.length > 0) {
    datasets.push(markerDataset);
  }

  graphData.value = {
    labels: [],
    datasets
  };
};
</script>

<template>
  <Dialog :visible="visible"
    modal
    :header="dialogLabel"
    class="dialog"
    :closeOnEscape="true"
    :draggable="false"
    style="max-width: 96%; overflow-x: auto"
    @update:visible="closeDialog">
    <div class="chart-root" v-if="graphData">
      <Chart :type="chartType"
        :data="graphData"
        :options="graphOptions"
        style="width: 1000px; height: 500px"/>

      <div class="group" v-if="chartType === 'line'">
        <div class="baseline-label">Show baseline data</div>
        <Checkbox v-model="showBaseLineData"
          binary
          inputId="baseline"
          name="baseline"
          value="baseline"/>
      </div>
    </div>
  </Dialog>
</template>

<style lang="scss" scoped>
.chart-root {
  width: 1000px;
  height: 550px;
  overflow: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  overflow-x: auto;
  overflow-y: hidden;
}
.group {
  display: flex;
  align-items: center;
  margin-top: 10px;

  .baseline-label {
    margin-right: 10px;
    font-weight: bold;
  }
}
</style>
